import {
  Button,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack
} from '@mui/material'
import { startCase } from 'lodash'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import GlyphIcon from 'components/common/GlyphIcon'
import Text from 'components/common/Text'
import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import { NOTIFICATION_LEFT_PADDING } from 'components/navigation/Notifications/utils'
import { ScenarioHaltConfirmationModal } from 'components/scenarios/detail/ScenarioActions'
import { getRunStage } from 'components/scenarios/utils'
import theme from 'theme'
import { ScenarioRun } from 'types/api/scenarios/runs/scenario-runs'

const TitleContent: React.FC<{ scenario: ScenarioRun }> = ({ scenario }) => {
  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column'
      }}
    >
      Scenario Completed
      <Text
        as="small"
        color="medium"
        css={{
          fontSize: theme.fontSizes[1],
          marginTop: theme.space[2]
        }}
      >
        <strong>{scenario.name || 'Your scenario'}</strong> has{' '}
        {getRunStage(scenario)}
      </Text>
    </div>
  )
}

export const CompletedScenarioListItem: React.FC<{
  scenario: ScenarioRun
  doFetch(): void
}> = ({ scenario, doFetch, ...rest }) => {
  const navigate = useNavigate()

  const runStage = getRunStage(scenario)

  const handleLinkClick = (event) => {
    event.stopPropagation()
    event.preventDefault()

    navigate(
      `/scenarios/detail/${scenario.scenario_id}/runs/${scenario.run_number}`,
      {
        state: {
          scrollToResults: true
        }
      }
    )
  }

  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleResultsClick: React.MouseEventHandler<HTMLButtonElement> = (
    event
  ) => {
    event.stopPropagation()
    event.preventDefault()
    setIsModalOpen(true)
  }

  return (
    <ListItem
      data-test-completed-scenario
      data-test-notification-item
      disablePadding
      component="div"
      {...rest}
    >
      <ListItemButton
        dense
        // note: in this case we can't use a router Link component with the `to` prop
        // because we have a child button component and we need to catch all clicks
        // and ensure we don't navigate when the button is clicked
        onClick={handleLinkClick}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{
            pl: NOTIFICATION_LEFT_PADDING,
            width: '100%',
            maxWidth: '100%',
            spacing: 1
          }}
        >
          <GlyphIcon css={{ marginRight: theme.space[3] }}>
            <Icon
              css={{
                fontSize: theme.fontSizes[4],
                color: theme.colors.gray[1],
                width: theme.space[6]
              }}
              icon="clipboardList"
            />
          </GlyphIcon>
          <ListItemText
            data-test-notification-text
            secondary={runStage ? startCase(runStage) : ''}
          >
            <TruncatedTextWithTooltip
              tooltipProps={{
                title: scenario.name
              }}
            >
              {scenario.name}
            </TruncatedTextWithTooltip>
          </ListItemText>
          {/*
            Note: this button is rendered here in the Stack because if it's rendered as a `secondaryAction`
            of the `ListItem` then the text overflow handling does not work correctly
          */}
          <Button
            data-test-input-results
            color="info"
            css={{ minWidth: 'fit-content' }}
            size="small"
            variant="outlined"
            onClick={handleResultsClick}
          >
            Input Results
          </Button>
        </Stack>
      </ListItemButton>

      {/*
        Important: this cannot exist as a child of another clickable element, like the ListItemButton above
        because when the modal is rendered, every click event within that modal will also be propagated to
        the button as well, causing a navigation event and breaking the modal.
      */}
      <ScenarioHaltConfirmationModal
        isCompletedNotification
        isScenarioRun
        confirmButtonType="primary"
        confirmText="Save"
        doFetch={doFetch}
        haltConfirmationOpen={isModalOpen}
        hideOverlay={false}
        scenario={scenario}
        scenarioId={scenario?.scenario_id}
        selectedRun={scenario}
        setDisableButton={() => true}
        setHaltConfirmationOpen={setIsModalOpen}
        shouldCloseOnOverlayClick={false}
        showHaltButton={false}
        title={<TitleContent scenario={scenario} />}
      />
    </ListItem>
  )
}
